// MESERO MÓVIL (/pos/rest-movil): vista del restaurante optimizada para el
// TELÉFONO del mesero — mesas → cuenta → agregar platos → enviar comanda →
// cobrar/facturar. Reutiliza las RPCs y los modales del Salón (misma lógica,
// mismo PIN de mesero compartido en sessionStorage). En el teléfono NO se
// imprime nada localmente: las estaciones con impresora de red salen por el
// print-agent; las de modo terminal se imprimen desde la caja/KDS.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bell, BellRing, ChefHat, Loader2, Plus, Receipt, RefreshCw, Smartphone, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { money, posRpc, useImpresionConfig } from './shared';
import {
  CuentaT, MESA_CLS, MESA_LABEL, MeseroSesionT, MesaT, ModGrupoT, RestEstadoT,
  estadoMesa, restEstadoQuery, useLocalRestaurant,
} from './restaurante';
import { AbrirModal, AnularModal, BuscarModal, CobroModal, ModsModal, PinModal, type ProductoBusqueda } from './Salon';

export function MeseroMovil() {
  useImpresionConfig();
  const qc = useQueryClient();
  const { locationId } = useLocalRestaurant();

  // Mismo mesero que el Salón (PIN validado, sobrevive F5 en esta pestaña)
  const [mesero, setMesero] = useState<MeseroSesionT | null>(() => {
    try { return JSON.parse(sessionStorage.getItem('rest_mesero') ?? 'null'); } catch { return null; }
  });
  useEffect(() => { if (mesero) sessionStorage.setItem('rest_mesero', JSON.stringify(mesero)); }, [mesero]);

  // Seguridad: si la app estuvo en segundo plano más de 5 minutos, se vuelve a
  // pedir el PIN del mesero (el teléfono pudo quedar desatendido).
  const ocultoDesde = useRef<number | null>(null);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') { ocultoDesde.current = Date.now(); return; }
      if (ocultoDesde.current && Date.now() - ocultoDesde.current > 5 * 60_000) {
        sessionStorage.removeItem('rest_mesero');
        setMesero(null);
        toast.info('Por seguridad, vuelve a ingresar tu PIN');
      }
      ocultoDesde.current = null;
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const [cuentaId, setCuentaId] = useState<string | null>(null);
  const [mPin, setMPin] = useState(false);
  const [mAbrir, setMAbrir] = useState<{ mesa: MesaT | null; llevar: boolean } | null>(null);
  const [mBuscar, setMBuscar] = useState(false);
  const [mMods, setMMods] = useState<{ prod: ProductoBusqueda; grupos: ModGrupoT[]; curso: number; asiento: number } | null>(null);
  const [mAnular, setMAnular] = useState<string | null>(null);
  const [mCobro, setMCobro] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const { data: estado, isFetching, refetch } = useQuery<RestEstadoT>(restEstadoQuery(locationId));

  // AVISOS (734): llamados de mesa desde la carta QR + platos LISTOS por retirar.
  // Vibra cuando aparece un aviso nuevo (best-effort; requiere interacción previa).
  interface Avisos {
    llamados: { id: string; mesa_numero: number; motivo: string; minutos: number; mesero_de_la_mesa: boolean }[];
    listas: { linea_id: string; cuenta_id: string; codigo: string; mesa_numero: number | null; descripcion: string; cantidad: number; minutos: number; mia: boolean }[];
  }
  const [listasOk, setListasOk] = useState<Set<string>>(new Set());  // avisos de plato listo ya vistos
  const avisosPrevios = useRef<Set<string>>(new Set());
  const { data: avisos } = useQuery<Avisos>({
    queryKey: ['rest-avisos', locationId, mesero?.id ?? null],
    queryFn: () => posRpc<Avisos>('rest_avisos_mesero', { _location: locationId, _mesero: mesero?.id ?? null }),
    enabled: !!locationId, refetchInterval: 10_000, staleTime: 0,
  });
  useEffect(() => {
    if (!avisos) return;
    const ids = new Set([...avisos.llamados.map((l) => `ll-${l.id}`), ...avisos.listas.map((l) => `li-${l.linea_id}`)]);
    const nuevos = [...ids].some((id) => !avisosPrevios.current.has(id));
    if (nuevos && avisosPrevios.current.size >= 0 && 'vibrate' in navigator) {
      try { navigator.vibrate([200, 100, 200]); } catch { /* noop */ }
    }
    avisosPrevios.current = ids;
  }, [avisos]);
  const listasVisibles = (avisos?.listas ?? []).filter((l) => !listasOk.has(l.linea_id));
  const atenderLlamado = async (id: string) => {
    try { await posRpc('rest_llamado_atender', { _id: id }); void qc.invalidateQueries({ queryKey: ['rest-avisos'] }); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const BarraAvisos = () => (
    <>
      {(avisos?.llamados ?? []).map((l) => (
        <div key={l.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${l.mesero_de_la_mesa ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'}`}>
          <BellRing className="w-4 h-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            Mesa {l.mesa_numero} {l.motivo === 'cuenta' ? 'pide la CUENTA' : 'llama al mesero'} · {l.minutos} min
          </span>
          <Button size="sm" variant="outline" onClick={() => void atenderLlamado(l.id)}>Atendido</Button>
        </div>
      ))}
      {listasVisibles.map((l) => (
        <div key={l.linea_id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${l.mia ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-border bg-muted/40'}`}>
          <Bell className="w-4 h-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            🍽 LISTO{l.mesa_numero != null ? ` · Mesa ${l.mesa_numero}` : ''}: {Number(l.cantidad)} × {l.descripcion} · {l.minutos} min
          </span>
          <Button size="sm" variant="ghost" onClick={() => setListasOk((prev) => new Set(prev).add(l.linea_id))}>OK</Button>
        </div>
      ))}
    </>
  );
  const { data: cuenta } = useQuery<CuentaT | null>({
    queryKey: ['rest-cuenta', cuentaId],
    queryFn: () => posRpc<CuentaT>('rest_cuenta', { _id: cuentaId }),
    enabled: !!cuentaId, staleTime: 0, refetchOnMount: 'always', refetchInterval: 8000,
  });
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['rest-estado', locationId] });
    if (cuentaId) void qc.invalidateQueries({ queryKey: ['rest-cuenta', cuentaId] });
  };

  const abrirCuenta = async (mesa: MesaT | null, tipo: string, comensales: number,
                             entrega: { nombre: string; telefono: string; direccion: string } | null) => {
    if (!mesero) { setMPin(true); return; }
    try {
      const c = await posRpc<CuentaT>('rest_cuenta_abrir', {
        _location: locationId, _mesa: mesa?.id ?? null, _tipo: mesa ? 'mesa' : tipo,
        _pin: mesero.pin, _comensales: comensales, _entrega: entrega,
      });
      invalidar();
      if (c) { setMAbrir(null); setCuentaId(c.id); }
    } catch (e) {
      const msg = getErrorMessage(e);
      if (/PIN de mesero inválido/i.test(msg)) {
        setMesero(null); setMAbrir(null); setMPin(true);
        toast.error('Tu PIN de mesero cambió o ya no es válido — vuelve a ingresarlo');
      } else toast.error(msg);
    }
  };

  // Steppers de cantidad (solo líneas SIN comandar); en 1, el − quita la línea
  const cambiarCantidad = async (lineaId: string, cantidad: number) => {
    try {
      if (cantidad <= 0) await posRpc('rest_linea_quitar', { _linea: lineaId });
      else await posRpc('rest_linea_cantidad', { _linea: lineaId, _cantidad: cantidad });
      invalidar();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const tocarMesa = (m: MesaT) => {
    if (m.cuenta) setCuentaId(m.cuenta.id);
    else setMAbrir({ mesa: m, llevar: false });
  };

  const enviarRonda = async () => {
    if (!cuentaId) return;
    setEnviando(true);
    try {
      const r = await posRpc<{ ronda: number; curso: number; retenidas: number;
        estaciones: { estacion: string; imprime_comanda: boolean; modo_impresion?: string }[] }>(
        'rest_ronda_enviar', { _cuenta: cuentaId });
      invalidar();
      const locales = r.estaciones.filter((e) => e.imprime_comanda && e.modo_impresion !== 'red').map((e) => e.estacion);
      toast.success(`Ronda ${r.ronda} enviada a cocina${Number(r.retenidas) > 0 ? ` · ${r.retenidas} línea(s) retenidas` : ''}`);
      if (locales.length) toast.info(`${locales.join(', ')}: impresora de la caja (la comanda no se imprime desde el teléfono; se ve en el KDS)`);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setEnviando(false); }
  };

  // Agrupación de líneas por ronda (null = pendientes de enviar)
  const rondas = useMemo(() => {
    const map = new Map<string, CuentaT['lineas']>();
    for (const l of cuenta?.lineas ?? []) {
      if (l.estado === 'anulada') continue;
      const k = l.ronda == null ? 'pendiente' : `Ronda ${l.ronda}`;
      map.set(k, [...(map.get(k) ?? []), l]);
    }
    return [...map.entries()];
  }, [cuenta]);
  const pendientes = (cuenta?.lineas ?? []).filter((l) => l.ronda == null && l.estado !== 'anulada').length;

  // ---------- VISTA CUENTA ----------
  if (cuentaId) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center gap-2 border-b p-3">
          <button className="rounded-md border p-2" onClick={() => { setCuentaId(null); invalidar(); }}><ArrowLeft className="w-5 h-5" /></button>
          <div className="min-w-0">
            <div className="font-semibold truncate">
              {cuenta?.mesa_numero != null ? `Mesa ${cuenta.mesa_numero}` : (cuenta?.tipo ?? 'Cuenta')} · {cuenta?.codigo ?? '…'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {cuenta?.mesero ?? '—'} · {cuenta?.comensales ?? 0} pax · {cuenta?.minutos ?? 0} min
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 space-y-3 pb-36">
          <BarraAvisos />
          {!cuenta && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          {rondas.map(([titulo, lineas]) => (
            <div key={titulo}>
              <div className={`text-xs font-semibold mb-1 ${titulo === 'pendiente' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                {titulo === 'pendiente' ? '⏳ Sin enviar' : titulo}
              </div>
              <div className="rounded-lg border divide-y">
                {lineas.map((l) => (
                  <div key={l.id} className="flex items-start gap-2 p-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">{Number(l.cantidad)} × {l.descripcion}</div>
                      {(l.modificadores ?? []).length > 0 && (
                        <div className="text-[11px] text-muted-foreground">• {(l.modificadores ?? []).map((m) => m.nombre).join(' · ')}</div>
                      )}
                      {l.notas && <div className="text-[11px] text-muted-foreground italic">"{l.notas}"</div>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-sm tabular-nums font-medium">{money(Number(l.subtotal))}</div>
                      {l.estado === 'pendiente' ? (
                        <div className="flex items-center gap-1">
                          <button className="h-8 w-8 rounded-md border text-lg leading-none"
                                  title={Number(l.cantidad) <= 1 ? 'Quitar línea' : 'Una menos'}
                                  onClick={() => void cambiarCantidad(l.id, Number(l.cantidad) - 1)}>−</button>
                          <span className="w-7 text-center text-sm tabular-nums">{Number(l.cantidad)}</span>
                          <button className="h-8 w-8 rounded-md border text-lg leading-none" title="Una más"
                                  onClick={() => void cambiarCantidad(l.id, Number(l.cantidad) + 1)}>+</button>
                        </div>
                      ) : !l.venta_id && (
                        <button className="rounded p-1.5 text-red-500" title="Anular línea (PIN supervisor)"
                                onClick={() => setMAnular(l.id)}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {cuenta && cuenta.lineas.filter((l) => l.estado !== 'anulada').length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">Cuenta vacía — agrega el primer plato.</p>
          )}
        </div>

        {/* Barra fija: totales + acciones grandes */}
        <div className="fixed inset-x-0 bottom-0 border-t bg-card p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Subtotal {money(Number(cuenta?.totales.subtotal ?? 0))}
              {Number(cuenta?.totales.servicio ?? 0) > 0 && <> · Serv. {money(Number(cuenta?.totales.servicio))}</>}
              {' '}· IVA {money(Number(cuenta?.totales.iva ?? 0))}
            </span>
            <span className="text-lg font-bold tabular-nums">{money(Number(cuenta?.totales.total ?? 0))}</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button className="h-12" variant="outline" onClick={() => setMBuscar(true)}><Plus className="w-5 h-5 mr-1" /> Plato</Button>
            <Button className="h-12" variant={pendientes > 0 ? 'default' : 'outline'} isLoading={enviando}
                    disabled={pendientes === 0} onClick={() => void enviarRonda()}>
              <ChefHat className="w-5 h-5 mr-1" /> Enviar{pendientes > 0 ? ` (${pendientes})` : ''}
            </Button>
            <Button className="h-12" disabled={!cuenta || cuenta.lineas.filter((l) => l.estado !== 'anulada').length === 0}
                    onClick={() => setMCobro(true)}>
              <Receipt className="w-5 h-5 mr-1" /> Cobrar
            </Button>
          </div>
        </div>

        {mBuscar && (
          <BuscarModal locationId={locationId} comensales={cuenta?.comensales ?? 4} onClose={() => setMBuscar(false)}
            onPick={async (p, curso, asiento) => {
              try {
                const grupos = await posRpc<ModGrupoT[]>('rest_producto_mods', { _producto: p.id });
                setMBuscar(false);
                if (grupos.length) setMMods({ prod: p, grupos, curso, asiento });
                else {
                  await posRpc('rest_linea_agregar', { _cuenta: cuentaId, _producto: p.id, _cantidad: 1,
                    _precio_base: p.precio_base, _modificadores: [], _notas: null, _curso: curso, _asiento: asiento || null });
                  invalidar();
                }
              } catch (e) { toast.error(getErrorMessage(e)); }
            }} />
        )}
        {mMods && (
          <ModsModal prod={mMods.prod} grupos={mMods.grupos} cursoInicial={mMods.curso}
            asientoInicial={mMods.asiento} comensales={cuenta?.comensales ?? 4} onClose={() => setMMods(null)}
            onOk={async (cantidad, opciones, notas, curso, asiento) => {
              try {
                await posRpc('rest_linea_agregar', { _cuenta: cuentaId, _producto: mMods.prod.id,
                  _cantidad: cantidad, _precio_base: mMods.prod.precio_base,
                  _modificadores: opciones.map((o) => ({ opcion_id: o })), _notas: notas || null,
                  _curso: curso, _asiento: asiento || null });
                setMMods(null); invalidar();
              } catch (e) { toast.error(getErrorMessage(e)); }
            }} />
        )}
        {mAnular && (
          <AnularModal onClose={() => setMAnular(null)} onOk={async (pin, motivo) => {
            try {
              await posRpc('rest_linea_anular', { _linea: mAnular, _pin_supervisor: pin, _motivo: motivo });
              setMAnular(null); invalidar();
              toast.success('Línea anulada');
            } catch (e) { toast.error(getErrorMessage(e)); }
          }} />
        )}
        {mCobro && cuenta && (
          <CobroModal cuenta={cuenta} locationId={locationId} onClose={() => setMCobro(false)}
            onDone={(cerrada: boolean) => { setMCobro(false); invalidar(); if (cerrada) setCuentaId(null); }} />
        )}
      </div>
    );
  }

  // ---------- VISTA MESAS ----------
  const mesasDe = (areaId: string) => (estado?.mesas ?? []).filter((m) => m.area_id === areaId).sort((a, b) => a.numero - b.numero);
  return (
    <div className="p-3 space-y-4 pb-10">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-semibold flex items-center gap-2"><Smartphone className="w-5 h-5" /> Mesero</h1>
        <button className="ml-auto inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm"
                onClick={() => setMPin(true)}>
          <User className="w-4 h-4" /> {mesero?.nombre ?? 'PIN'}
        </button>
        <button className="rounded-md border p-2" onClick={() => void refetch()}>
          {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      <BarraAvisos />

      {(estado?.areas ?? []).map((a) => (
        <div key={a.id}>
          <div className="text-xs font-semibold text-muted-foreground mb-1.5">{a.nombre}</div>
          <div className="grid grid-cols-3 gap-2">
            {mesasDe(a.id).map((m) => {
              const st = estadoMesa(m);
              return (
                <button key={m.id} onClick={() => tocarMesa(m)}
                        className={`rounded-lg border p-2 text-left min-h-[4.5rem] ${MESA_CLS[st]}`}>
                  <div className="font-bold">M{m.numero}</div>
                  <div className="text-[11px] leading-tight opacity-90">
                    {m.cuenta ? <>{money(Number(m.cuenta.total))}<br />{m.cuenta.minutos} min</> : MESA_LABEL[st]}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-1.5">Para llevar</div>
        <div className="grid grid-cols-3 gap-2">
          {(estado?.llevar ?? []).map((c) => (
            <button key={c.id} onClick={() => setCuentaId(c.id)}
                    className="rounded-lg border p-2 text-left min-h-[4.5rem] bg-sky-50 border-sky-300 text-sky-900 dark:bg-sky-900/20 dark:border-sky-800 dark:text-sky-200">
              <div className="font-bold truncate">{c.codigo}</div>
              <div className="text-[11px]">{money(Number(c.total))} · {c.minutos} min</div>
            </button>
          ))}
          <button onClick={() => setMAbrir({ mesa: null, llevar: true })}
                  className="rounded-lg border border-dashed p-2 min-h-[4.5rem] text-sm text-muted-foreground">
            <Plus className="w-5 h-5 mx-auto" /> Nueva
          </button>
        </div>
      </div>

      {mPin && <PinModal onClose={() => setMPin(false)} onOk={(m: MeseroSesionT) => { setMesero(m); setMPin(false); }} />}
      {mAbrir && (
        <AbrirModal mesa={mAbrir.mesa} llevar={mAbrir.llevar} mesero={mesero}
          onPin={() => setMPin(true)} onClose={() => setMAbrir(null)}
          onOk={(comensales: number, tipo: string, entrega: { nombre: string; telefono: string; direccion: string } | null) =>
            void abrirCuenta(mAbrir.mesa, tipo, comensales, entrega)} />
      )}
    </div>
  );
}
